import { KnowledgeArticle, createArticle } from '../types'

export const assetsArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'asset-inventory',
    title: 'Asset Inventory',
    section: 'assets',
    summary: 'Track IT assets including servers, applications, databases, and cloud resources. Assets are auto-discovered from integrations or added manually.',
    content: `
# Asset Inventory

The Asset Inventory provides a centralized view of your organization's IT assets for compliance and security management.

## What Are Assets?

Assets include any IT resource that:
- Stores or processes data
- Supports business operations
- Requires security controls
- Falls under compliance scope

## Asset Types

### Hardware
- Servers (physical and virtual)
- Workstations and laptops
- Network devices (routers, switches, firewalls)
- Storage systems

### Software
- Operating systems
- Applications
- Databases
- Middleware

### Cloud Resources
- Virtual machines (EC2, Azure VMs)
- Databases (RDS, Azure SQL)
- Storage (S3, Blob)
- Containers and clusters

### Data
- Databases and data stores
- File shares
- Backups
- Archives

## Asset Properties

Each asset includes:
- **Name** - Identifier for the asset
- **Type** - Category of asset
- **Owner** - Person responsible
- **Criticality** - Business importance (Critical, High, Medium, Low)
- **Environment** - Production, Staging, Development
- **Data Classification** - Public, Internal, Confidential, Restricted
- **Tags** - Custom labels for grouping

## Auto-Discovery

Assets are automatically discovered from:
- **AWS Integration** - EC2, RDS, S3
- **Azure Integration** - VMs, SQL, Storage
- **Vulnerability Scans** - Hosts and services

## Dashboard View

The Assets page shows:
- Total asset count by type
- Assets by criticality
- Assets by environment
- Recently discovered assets
    `.trim(),
    tags: ['assets', 'inventory', 'discovery', 'hardware', 'software', 'cloud'],
    relatedPages: ['/assets'],
    icon: 'Server',
    order: 1,
  }),

  createArticle({
    id: 'managing-assets',
    title: 'Managing Assets',
    section: 'assets',
    summary: 'Add assets manually or import from CSV. Assign ownership, set criticality, link to controls, and track asset lifecycle.',
    content: `
# Managing Assets

Learn how to add, organize, and maintain your asset inventory.

## Adding Assets

### Manual Entry
1. Go to **Assets**
2. Click **Add Asset**
3. Fill in asset details:
   - Name and description
   - Asset type
   - Owner
   - Criticality level
   - Environment
   - Data classification
4. Click **Create Asset**

### CSV Import
Import multiple assets at once:
1. Click **Import Assets**
2. Download the template CSV
3. Fill in asset data
4. Upload the completed CSV
5. Review and confirm import

### Auto-Discovery
Assets are created automatically from:
- Cloud integration syncs
- Vulnerability scan results
- These appear as "Discovered" status

## Asset Details

### Overview Tab
- Basic information
- Owner and contacts
- Criticality and classification

### Controls Tab
- Linked compliance controls
- Control status for this asset
- Coverage gaps

### Vulnerabilities Tab
- Findings from scans
- Severity breakdown
- Remediation status

### History Tab
- Changes over time
- Who made changes
- Audit trail

## Asset Lifecycle

### Status Values
- **Active** - In use, fully managed
- **Discovered** - Auto-discovered, needs review
- **Decommissioning** - Being phased out
- **Decommissioned** - No longer in use

### Decommissioning Process
1. Change status to "Decommissioning"
2. Complete decommission checklist
3. Remove from compliance scope
4. Mark as "Decommissioned"
5. Retain record for audit

## Asset Grouping

### Using Tags
Add tags to group assets:
- By team (engineering, sales, etc.)
- By application (app-frontend, app-backend)
- By compliance scope (pci-scope, hipaa-scope)

### Filtering
Filter the asset list by:
- Type
- Criticality
- Environment
- Owner
- Tags
- Status

## Linking to Controls

Connect assets to relevant controls:
1. Open the asset
2. Go to **Controls** tab
3. Click **Link Controls**
4. Select applicable controls
5. Track control status per asset

## Best Practices

1. Keep inventory current
2. Assign clear ownership
3. Review discovered assets promptly
4. Use consistent naming conventions
5. Tag for easy filtering
6. Link to compliance scope
7. Track decommissioned assets
    `.trim(),
    tags: ['assets', 'management', 'import', 'lifecycle', 'controls', 'tags'],
    relatedPages: ['/assets'],
    icon: 'Settings',
    order: 2,
  }),

  createArticle({
    id: 'asset-scanning',
    title: 'Asset Scanning Integration',
    section: 'assets',
    summary: 'Assets discovered from vulnerability scans are added to inventory. Link scan findings to assets for tracking vulnerabilities per asset.',
    content: `
# Asset Scanning Integration

Assets and vulnerability scanning work together to provide comprehensive security visibility.

## How It Works

### Discovery Flow
1. Run a vulnerability scan
2. Scanner discovers hosts and services
3. New assets are created in inventory
4. Findings are linked to assets

### Asset Creation
When a scan finds a new host:
- Asset is created with "Discovered" status
- Type is set based on detected services
- Hostname/IP is recorded
- You review and enrich the asset details

## Viewing Scan Results

### From Asset View
1. Open an asset
2. Go to **Vulnerabilities** tab
3. See all findings for this asset
4. Filter by severity or status

### From Scan Results
1. Go to **Scanners**
2. View scan results
3. Click on a finding
4. See the linked asset

## Enriching Discovered Assets

After discovery, add business context:
1. Find assets with "Discovered" status
2. Review and verify accuracy
3. Add:
   - Friendly name
   - Owner assignment
   - Criticality level
   - Environment
   - Data classification
4. Change status to "Active"

## Matching Logic

Scans match to existing assets by:
1. IP address (exact match)
2. Hostname (case-insensitive)
3. FQDN (fully qualified domain name)

If no match is found, a new asset is created.

## Vulnerability Tracking

### Per-Asset View
Track vulnerabilities for each asset:
- Total vulnerabilities
- By severity (critical, high, medium, low)
- Remediation progress
- Trend over time

### Compliance Impact
Asset vulnerabilities affect:
- Control status (vulnerability management)
- Risk assessments
- Audit readiness

## Best Practices

1. Review discovered assets promptly
2. Enrich with business context
3. Merge duplicates if found
4. Keep owner assignments current
5. Use criticality to prioritize remediation
    `.trim(),
    tags: ['assets', 'scanning', 'vulnerabilities', 'discovery', 'integration'],
    relatedPages: ['/assets', '/scanners'],
    icon: 'Radar',
    order: 3,
  }),
]
